import { useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import { CompactChartLegend } from '@trinity/chat-components/components/Widgets/Charts/CompactLegend';
import { buildKpiTableRows, buildWidgetTableRows, type KpiQuickChartData } from '../../utils/widget-chart';
import { AddToDashboardButton } from '../Chat/Chat.Chart.AddToDashboard';
import { ChartFilterPane } from '../Chat/Chat.Chart.Settings';
import { ChartViewButton, TableViewButton } from '../Chat/Chat.Chat.ViewToggle';
import { ChartInfoContext, ChartTypeContext, ChartViewContext } from '../Chat/context';
import { useRenderResponseChart, useResponseChartLegend } from '../Chat/Message.Response.Chart.hooks';
import { KPIWidget } from '../Widgets/KPIWidget';
import { WidgetDataTable } from './WidgetDataTable';

export function WidgetChartView({ kpi, title }: { kpi?: KpiQuickChartData; title: string }) {
  const [viewMode, setViewMode] = useContext(ChartViewContext);

  return (
    <Root>
      <Header>
        <Title>{title}</Title>
        <ButtonBar>
          <ViewToggleGroup>
            <ChartViewButton onClick={() => setViewMode('chart')} viewMode="chart" isActive={viewMode === 'chart'} />
            <TableViewButton onClick={() => setViewMode('table')} viewMode="table" isActive={viewMode === 'table'} />
          </ViewToggleGroup>
          <ActionButtonGroup>
            {kpi ? (
              <AddToDashboardButton quickChartData={kpi} chartTitle={kpi.title} />
            ) : (
              <AddToDashboardButton />
            )}
            {!kpi && <ChartFilterPane />}
          </ActionButtonGroup>
        </ButtonBar>
      </Header>

      {kpi ? <KpiBody kpi={kpi} viewMode={viewMode} /> : <ChartBody viewMode={viewMode} />}
    </Root>
  );
}

function KpiBody({ kpi, viewMode }: { kpi: KpiQuickChartData; viewMode: 'chart' | 'table' }) {
  if (viewMode === 'table') {
    return <WidgetDataTable model={buildKpiTableRows(kpi.values, kpi.labels)} />;
  }
  return (
    <KpiArea>
      <KPIWidget
        title={kpi.title}
        backgroundColor={kpi.backgroundColor}
        values={kpi.values}
        labels={kpi.labels}
      />
    </KpiArea>
  );
}

function ChartBody({ viewMode }: { viewMode: 'chart' | 'table' }) {
  const [chart] = useContext(ChartInfoContext);
  const [chartType] = useContext(ChartTypeContext);

  const legend = useResponseChartLegend();
  const renderChart = useRenderResponseChart({ legend, unitInfo: null });

  const tableModel = useMemo(
    () => buildWidgetTableRows(chartType, chart.chart.chart),
    [chartType, chart],
  );

  if (viewMode === 'table') {
    return <WidgetDataTable model={tableModel} />;
  }
  return (
    <>
      <ChartArea>{renderChart(chartType)}</ChartArea>
      {legend.length > 0 && <CompactChartLegend legend={legend} />}
    </>
  );
}

WidgetChartView.displayName = 'Chat.Attachments.WidgetChartView';

const Root = styled.div(({ theme }) => ({
  border: `1px solid ${theme.palette.gray.light1}`,
  borderRadius: 15,
  padding: 12,
  marginTop: 10,
  backgroundColor: theme.palette.white.main,
}));
const Header = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  marginBottom: 10,
});
const Title = styled.div(({ theme }) => ({ fontFamily: theme.fonts.semiBold, textAlign: 'center' }));
const ButtonBar = styled.div({ display: 'flex', justifyContent: 'space-between', alignItems: 'center' });
const ViewToggleGroup = styled.div({ display: 'flex', alignItems: 'center', gap: 12 });
const ActionButtonGroup = styled.div({ display: 'flex', alignItems: 'center', gap: 12 });
const ChartArea = styled.div({ height: 320, width: '100%' });
const KpiArea = styled.div({ width: '100%' });
